import { parseDDMMYYYYToLocal, toLocalDateKey } from "../../../utils/DatesHelpers";

/** Check if date (Date or DD/MM/YYYY string) is in list (array of DD/MM/YYYY strings). */
export function toNormalizedYmd(dateOrStr) {
  if (!dateOrStr) return "";
  if (dateOrStr instanceof Date) {
    return toLocalDateKey(
      new Date(
        dateOrStr.getFullYear(),
        dateOrStr.getMonth(),
        dateOrStr.getDate(),
        12,
        0,
        0
      )
    );
  }
  if (typeof dateOrStr !== "string") return "";
  const parsed = parseDDMMYYYYToLocal(dateOrStr);
  if (!parsed) return "";
  return toLocalDateKey(
    new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 12, 0, 0)
  );
}

export function normalizeDateListToYmdSet(list) {
  if (!Array.isArray(list)) return null;
  const normalized = list.map((d) => toNormalizedYmd(d)).filter(Boolean);
  return new Set(normalized);
}

export function isDateInList(dateOrStr, normalizedSet) {
  if (normalizedSet == null) return true;
  if (normalizedSet.size === 0) return false;
  const normalized = toNormalizedYmd(dateOrStr);
  if (!normalized) return false;
  return normalizedSet.has(normalized);
}

/** Fast path: cell key is already YYYY-MM-DD. */
export function isYmdAllowed(ymd, normalizedSet) {
  if (normalizedSet == null) return true;
  if (normalizedSet.size === 0) return false;
  return Boolean(ymd) && normalizedSet.has(ymd);
}

export function formatMonthKey(year, monthIndex) {
  return `${String(monthIndex + 1).padStart(2, "0")}/${year}`;
}

/** Parse `MM/YYYY` (also accepts `M/YYYY`) into a sorted-tile model. */
export function parseMonthKey(monthKey) {
  const raw = String(monthKey || "").trim();
  const match = raw.match(/^(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const year = Number(match[2]);
  if (!Number.isFinite(month) || month < 1 || month > 12 || !Number.isFinite(year)) {
    return null;
  }
  return {
    year,
    monthIndex: month - 1,
    key: formatMonthKey(year, month - 1),
  };
}

/**
 * Months to offer as tiles in whole-month mode.
 * Prefer searchable months when provided; otherwise the upcoming window.
 */
export function buildMonthTileModels({
  availableMonths = null,
  monthsAhead = 12,
  from = new Date(),
} = {}) {
  const parsed = Array.isArray(availableMonths)
    ? availableMonths.map((item) => parseMonthKey(item)).filter(Boolean)
    : [];
  const source =
    parsed.length > 0
      ? parsed
      : buildMonthsToShow(monthsAhead, from).map(({ year, month }) => ({
          year,
          monthIndex: month,
          key: formatMonthKey(year, month),
        }));
  const seen = new Set();
  const tiles = [];
  for (const item of source) {
    if (seen.has(item.key)) continue;
    seen.add(item.key);
    tiles.push(item);
  }
  tiles.sort((a, b) => a.year - b.year || a.monthIndex - b.monthIndex);
  return tiles;
}

export function pad2(n) {
  return String(n).padStart(2, "0");
}

export function toCellKey(year, monthIndex, day) {
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}

export function toDateStrIL(year, monthIndex, day) {
  return `${pad2(day)}/${pad2(monthIndex + 1)}/${year}`;
}

export function startOfTodayKey(from = new Date()) {
  return `${from.getFullYear()}-${pad2(from.getMonth() + 1)}-${pad2(from.getDate())}`;
}

export function buildMonthsToShow(ahead = 12, from = new Date()) {
  const months = [];
  const startYear = from.getFullYear();
  const startMonth = from.getMonth();
  for (let i = 0; i < ahead; i++) {
    const d = new Date(startYear, startMonth + i, 1);
    const year = d.getFullYear();
    const month = d.getMonth();
    months.push({
      year,
      month,
      label: `${year}-${pad2(month + 1)}`,
    });
  }
  return months;
}

export function getMonthGridMeta(year, month) {
  const first = new Date(year, month, 1);
  return {
    startDay: first.getDay(),
    daysInMonth: new Date(year, month + 1, 0).getDate(),
  };
}

export function priceToneClass(amount, priceBand) {
  if (!priceBand || amount == null) return null;
  const ratio = (amount - priceBand.pMin) / priceBand.span;
  if (ratio <= 0.34) return "drp-day-price--low";
  if (ratio >= 0.66) return "drp-day-price--high";
  return "drp-day-price--mid";
}

export function getHoverPreviewState(
  cellKey,
  { hoverKey, arrivalKey, pickingReturn, disabled }
) {
  if (disabled || !pickingReturn || !hoverKey) {
    return { isPreviewEnd: false, isPreviewBetween: false };
  }
  return {
    isPreviewEnd: cellKey === hoverKey,
    isPreviewBetween: Boolean(
      arrivalKey && cellKey > arrivalKey && cellKey < hoverKey
    ),
  };
}

export function buildMonthDayModels({
  year,
  month,
  todayKey,
  arrivalKey,
  departureKey,
  isSettingArrival,
  unrestrictedReturnDates,
  arrivalSet,
  departureSet,
  pricesByDate,
  priceToneBand,
  priceBand,
  isMonthMode,
  monthSelectable,
}) {
  const { startDay, daysInMonth } = getMonthGridMeta(year, month);
  const days = [];

  for (let d = 1; d <= daysInMonth; d++) {
    const cellKey = toCellKey(year, month, d);
    const dateStr = toDateStrIL(year, month, d);
    const isPast = cellKey < todayKey;
    const isArrival = Boolean(arrivalKey) && cellKey === arrivalKey;
    const isDeparture = Boolean(departureKey) && cellKey === departureKey;
    const isBetween = Boolean(
      arrivalKey &&
        departureKey &&
        cellKey > arrivalKey &&
        cellKey < departureKey
    );
    const allowedArrival = !isPast && isYmdAllowed(cellKey, arrivalSet);
    const allowedDeparture =
      !isPast &&
      Boolean(arrivalKey) &&
      cellKey > arrivalKey &&
      (unrestrictedReturnDates || isYmdAllowed(cellKey, departureSet));
    const disabled =
      (isSettingArrival ? !allowedArrival : !allowedDeparture) ||
      (isMonthMode && !monthSelectable);
    const datePrice = pricesByDate ? pricesByDate[dateStr] : undefined;
    const hasPrice = datePrice != null && datePrice > 0;
    const pRounded = hasPrice ? Math.round(datePrice) : null;
    const toneCls =
      priceToneBand && hasPrice && !disabled
        ? priceToneClass(pRounded, priceBand)
        : null;

    days.push({
      cellKey,
      dayNum: d,
      dateStr,
      disabled,
      isArrival,
      isDeparture,
      isBetween,
      hasPrice,
      pRounded,
      toneCls,
    });
  }

  return { startDay, days };
}

export function syncHoverPreviewClasses(
  root,
  hoverKey,
  { arrivalKey, pickingReturn }
) {
  if (!root) return;
  const nodes = root.querySelectorAll("[data-day-key]");
  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    const cellKey = el.getAttribute("data-day-key");
    const disabled = el.classList.contains("drp-day-disabled");
    const next = getHoverPreviewState(cellKey, {
      hoverKey,
      arrivalKey,
      pickingReturn,
      disabled,
    });
    const hasEnd = el.classList.contains("drp-day-preview-end");
    const hasBetween = el.classList.contains("drp-day-preview-between");
    if (next.isPreviewEnd !== hasEnd) {
      el.classList.toggle("drp-day-preview-end", next.isPreviewEnd);
    }
    if (next.isPreviewBetween !== hasBetween) {
      el.classList.toggle("drp-day-preview-between", next.isPreviewBetween);
    }
  }
}
